import { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, Brain, CheckCircle2, FileText, PlayCircle, RefreshCw } from "lucide-react";
import { useAppOutlet } from "../components/Shell";
import { primaryButtonClass, Status } from "../components/ui";
import { localizedTitle } from "../lib/localize";
import { itemLabelKey } from "../lib/pdDayRoutes";
import {
  downloadState,
  ensureModuleDownloaded,
  subscribeDownloads,
} from "../offline/moduleDownload";
import { usePDDays } from "../pddays/usePDDays";

function downloadAll(moduleKey: string): void {
  moduleKey
    .split(",")
    .filter(Boolean)
    .forEach((id) => void ensureModuleDownloaded(id));
}

const itemIcon = (type: string) =>
  type === "video" ? PlayCircle : type === "quiz" ? Brain : FileText;

// The module's number is shown on its own ("Module 1"), so a title authored
// as "Module 1: What are Values?" would repeat it; keep just the name.
export function moduleName(title: string): string {
  return title.replace(/^\s*(?:module|ماڈیول)\s*\d+\s*[:.\-–—،]\s*/i, "").trim() || title;
}

// The first screen of a teacher's PD Day: every module's items in order,
// saved to the phone in the background. No download progress is shown; the
// teacher only hears about it when something could not be saved.
export function PDDayModules() {
  const { t, i18n } = useTranslation();
  const { sessionID = "" } = useParams();
  const { setHeaderTitle } = useAppOutlet();
  const { all, dayNumbers, loading, error } = usePDDays();
  const day = all.find((entry) => entry.id === sessionID);
  const dayNumber = dayNumbers[sessionID];
  const modules = day?.modules ?? [];
  const moduleKey = modules.map((module) => module.id).join(",");
  const [, setTick] = useState(0);
  const headerTitle = t("pdDayOrdinal", { number: dayNumber ?? "" });

  useEffect(() => {
    setHeaderTitle(headerTitle);
    return () => setHeaderTitle(null);
  }, [headerTitle, setHeaderTitle]);

  useEffect(() => subscribeDownloads(() => setTick((value) => value + 1)), []);

  // Joins the header's background download when it already started.
  const download = () => downloadAll(moduleKey);
  useEffect(() => downloadAll(moduleKey), [moduleKey]);

  if (loading) {
    return <Status variant="loading" title={t("loading")} message={t("loadingPDDay")} />;
  }
  if (error || !day) {
    return (
      <Status
        variant="error"
        title={t("pdDayUnavailable")}
        message={error || t("noAssignedPDDayHelp")}
      />
    );
  }
  if (modules.length === 0) {
    return <Status variant="empty" title={t("emptyModule")} message={t("noModulesYet")} />;
  }

  const states = modules.map((module) => downloadState(module.id));
  const failed = states.some((state) => state.phase === "error");
  const details = states.map((state) => state.module);
  const ready = details.every(Boolean);
  const groups = ready
    ? details.map((detail, index) => ({
        module: modules[index],
        moduleNumber: index + 1,
        items: detail!.items,
      }))
    : [];
  // Carry on from the first module not yet submitted.
  const resume = modules.find((module) => !module.submitted) ?? modules[0];

  return (
    <div className="flex flex-col gap-5 pb-28 pt-2">
      <h1 className="px-1 text-xl font-semibold tracking-tight text-app-ink">
        {t("pdDayContentTitle", { number: dayNumber ?? "" })}
      </h1>

      {failed && (
        <div
          className="flex flex-col gap-3 rounded-xl bg-warning-bg p-4 text-warning-text"
          role="alert"
        >
          <p className="flex items-start gap-2 text-sm font-medium">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            {t("pdDayDownloadFailed")}
          </p>
          <button
            type="button"
            className="inline-flex min-h-10 items-center justify-center gap-2 self-start rounded-lg bg-surface px-4 text-sm font-semibold text-app-ink shadow-raised"
            onClick={download}
          >
            <RefreshCw size={16} />
            {t("retry")}
          </button>
        </div>
      )}

      {ready
        ? groups.map(({ module, moduleNumber, items }) => (
            <section key={module.id} className="flex flex-col gap-2">
              <div className="px-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-accent">
                  {t("moduleNumber", { number: moduleNumber })}
                </span>
                <h2 className="text-base font-semibold leading-snug text-app-ink">
                  {moduleName(localizedTitle(module, i18n.language))}
                </h2>
              </div>
              <ol className="flex flex-col overflow-hidden rounded-xl bg-surface shadow-raised">
                {items.map((item) => {
                  const Icon = itemIcon(item.type);
                  return (
                    <li
                      key={item.id}
                      className="flex items-center gap-3 border-b border-app-line px-4 py-3.5 last:border-b-0"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                        <Icon size={18} />
                      </span>
                      <span className="min-w-0 flex-1 text-base font-semibold text-app-ink">
                        {t(itemLabelKey(item.type), { number: moduleNumber })}
                      </span>
                      {item.progress.status === "completed" && (
                        <CheckCircle2
                          size={22}
                          className="shrink-0 text-success-text"
                          aria-label={t("moduleCompleted")}
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            </section>
          ))
        : !failed && <Status variant="loading" title={t("loading")} message={t("loadingPDDay")} />}

      <div className="fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 bg-app-canvas/90 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] backdrop-blur-md">
        <NavLink
          to={`/modules/${encodeURIComponent(resume.id)}`}
          className={`${primaryButtonClass} flex items-center justify-center gap-2 text-sm`}
        >
          {t("continueToPDDay", { number: dayNumber ?? "" })}
          <ArrowRight size={18} className="rtl:rotate-180" />
        </NavLink>
      </div>
    </div>
  );
}
